import { Checkbox, FormControl, FormControlLabel, FormControlProps, Box } from '@mui/material';
import { FormErrorText } from '@/components/forms/other/ErrorText/FormErrorText';
import { useController, FieldError, Path, Control, Controller } from 'react-hook-form';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useTheme } from '@mui/material';
import { JSXFromString } from '@/utils';

interface CustomCheckboxProps<T extends object> {
    control: Control<T>;
    name: Path<T>;
    label: React.ReactNode;
    labelReplacements?: number[];
    error?: FieldError | null;
    value?: boolean;
    required?: boolean;
}

type CheckboxFieldProps<T extends object> = CustomCheckboxProps<T> &
    Omit<FormControlProps, 'error' | 'label' | 'name'>;

export const CheckboxField = <T extends object>(props: CheckboxFieldProps<T>) => {
    const { t } = usePhrase();
    const theme = useTheme();
    const { label, labelReplacements, control, name, error, required, ...rest } = props;
    const {
        fieldState: { error: fieldError },
    } = useController({
        name,
        control,
    });
    const replaceTextVars = labelReplacements?.map((phraseId) => t(phraseId) as string);
    return (
        <FormControl sx={{ marginBottom: rest.margin === 'none' ? 0 : 'initial' }} {...rest}>
            <FormControlLabel
                label={
                    typeof label === 'number' ? JSXFromString(t(label, { replaceTextVars })) : label
                }
                required={required}
                control={
                    <Controller
                        name={name}
                        control={control}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <Checkbox
                                name={name}
                                onBlur={onBlur}
                                value={value}
                                checked={!!value}
                                onChange={() => {
                                    onChange(!value);
                                }}
                                inputRef={ref}
                                style={{ color: error ? theme.palette.error.main : undefined }}
                            />
                        )}
                    />
                }
            />
            <Box sx={{ ml: 1.8 }}>
                <FormErrorText error={props.error || fieldError} />
            </Box>
        </FormControl>
    );
};
